import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FeedSection.css';
import {
  FaArrowUp,
  FaArrowRight,
  FaMapMarkerAlt,
  FaRegEdit,
  FaThumbsUp,
  FaCommentDots,
  FaTrash,
  FaChevronDown,
  FaTimes,
  FaTruck,
  FaWarehouse,
  FaPassport,
  FaRoute,
} from 'react-icons/fa';
import { FiAlertTriangle, FiMapPin, FiShield, FiStar } from 'react-icons/fi';
import usePromotionLookup from '../../../hooks/usePromotionLookup';
import { listPublishedRequests, updateRequest } from '../../../services/requestsApi';
import {
  listPublishedPublications,
  updatePublication,
  registerPublicationFeedView,
} from '../../../services/publicationsApi';
import { listFeedPosts, deleteFeedPost, updateFeedPost } from '../../../services/feedPostsApi';
import { getCompanyById, listPublishedCompanies } from '../../../services/companyApi';
import { buildUserAvatarUrl } from '../../../services/profileApi';
import { createRequestProposal } from '../../../services/requestProposalsApi';
import { createNotification } from '../../../services/notificationsApi';
import FeedNewsList from './FeedNewsList';
import {
  PROVIDER_CATEGORIES,
  buildPublicationFeedItem,
  buildRequestFeedItem,
} from '../../../utils/feedItems';
import FeedComposer from './FeedComposer';
import { fetchReactionStats, toggleReaction } from '../../../services/feedReactionsApi';

const formatRelativeTime = iso => {
  if (!iso) return '';
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return '';
  const now = new Date();
  const diff = now - target;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return 'hace instantes';
  if (diff < hour) {
    const minutes = Math.round(diff / minute);
    return `hace ${minutes} min`;
  }
  if (diff < day) {
    const hours = Math.round(diff / hour);
    return `hace ${hours} h`;
  }
  const days = Math.round(diff / day);
  return `hace ${days} d`;
};

const currencyFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const formatPriceValue = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `$${currencyFormatter.format(value)}`;
  }
  const numeric = Number(String(value || '').replace(/[^\d.-]/g, ''));
  if (Number.isFinite(numeric)) {
    return `$${currencyFormatter.format(numeric)}`;
  }
  return String(value || '').trim();
};

const renderCardHeader = ({
  avatarNode,
  name,
  subtitle,
  timestampLabel,
  timestampDate,
  typeLabel,
  statusLabel,
  actions,
}) => (
  <header className="feed-card__shell-header">
    <div className="feed-card__shell-left">
      {avatarNode}
      <div className="feed-card__shell-text">
        <span className="feed-card__shell-name">{name}</span>
        {subtitle ? <span className="feed-card__shell-subtitle">{subtitle}</span> : null}
      </div>
    </div>
    <div className="feed-card__shell-right">
      <div className="feed-card__shell-meta">
        {timestampLabel ? (
          <time className="feed-card__shell-time" dateTime={timestampDate || timestampLabel}>
            {timestampLabel}
          </time>
        ) : null}
        {statusLabel ? <span className="feed-card__status-pill">{statusLabel}</span> : null}
        {typeLabel ? <span className="feed-card__type-badge">{typeLabel}</span> : null}
      </div>
      {actions ? <div className="feed-card__shell-actions">{actions}</div> : null}
    </div>
  </header>
);

const renderCardFooter = ({
  priceLabel,
  priceEyebrow,
  reacted,
  reactionTotal,
  onToggleReaction,
  onComment,
  primaryLabel,
  onPrimaryAction,
  primaryDisabled = false,
}) => (
  <footer className="feed-card__shell-footer">
    <div className="feed-card__footer-left">
      {priceLabel ? (
        <div className="feed-card__footer-price">
          {priceEyebrow ? <span className="feed-card__footer-price-label">{priceEyebrow}</span> : null}
          <span className="feed-card__footer-price-value">{priceLabel}</span>
        </div>
      ) : null}
      <div className="feed-card__footer-engagement">
        <button
          type="button"
          className={`feed-card__icon-action${reacted ? ' feed-card__icon-action--active' : ''}`}
          data-animating={reacted ? 'true' : 'false'}
          onClick={onToggleReaction}
        >
          <FaThumbsUp />
          {reactionTotal > 0 ? <span>{reactionTotal}</span> : null}
        </button>
        <button
          type="button"
          className="feed-card__icon-action"
          onClick={onComment}
          disabled={!onComment}
          aria-label="Ver comentarios"
        >
          <FaCommentDots />
        </button>
      </div>
    </div>
    {primaryLabel ? (
      <button
        type="button"
        className="feed-card__primary-cta"
        onClick={onPrimaryAction}
        disabled={!onPrimaryAction || primaryDisabled}
      >
        {primaryLabel}
      </button>
    ) : null}
  </footer>
);

const toTimestamp = value => {
  if (!value) return 0;
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const resolveCreatedAt = entry => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') {
    const candidates = [
      'createdAt',
      'created',
      'created_on',
      'createdOn',
      'publishedAt',
      'published_at',
      'publicationDate',
      'creationDate',
    ];
    for (const key of candidates) {
      const value = entry[key];
      if (value) return value;
    }
  }
  return '';
};

const isDateBeforeToday = value => {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed.getTime() < today.getTime();
};

const isRequestExpired = request => {
  if (!request) return false;
  const referenceDate = request.neededBy || request.deadline || request.deliveryDate || resolveCreatedAt(request);
  return isDateBeforeToday(referenceDate);
};

const getCompanyInitial = name => {
  if (!name) return 'F';
  return name.trim().charAt(0).toUpperCase();
};

const LETTER_REGEX = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/;

const normalizeSentenceCase = input => {
  if (typeof input !== 'string' || !input) {
    return '';
  }

  let result = '';
  let shouldCapitalize = true;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];

    if (LETTER_REGEX.test(char)) {
      result += shouldCapitalize
        ? char.toLocaleUpperCase('es-MX')
        : char.toLocaleLowerCase('es-MX');
      shouldCapitalize = false;
    } else {
      result += char;
      if (char === '.' || char === '!' || char === '?' || char === '\n' || char === '\r' || char === '¡' || char === '¿') {
        shouldCapitalize = true;
      }
    }
  }

  if (!result) {
    return '';
  }

  return result.replace(/\b([a-záéíóúüñ]{1,3})\b/gi, (match, word, offset) => {
    const originalSlice = input.slice(offset, offset + word.length);
    if (/^[A-ZÁÉÍÓÚÜÑ]{1,3}$/.test(originalSlice)) {
      return originalSlice;
    }
    return match;
  });
};

const stripHtml = value =>
  typeof value === 'string'
    ? value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    : '';

const normalizeText = value =>
  value ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

const PUBLICATION_TYPE_PLACEHOLDERS = {
  service_ground_transport: '/Images/Full.png',
  service_security: '/Images/Custodia.png',
  service_road_assistance: '/Images/Grua.png',
  product: '/Images/Proyecto.png',
};

const PUBLICATION_CATEGORY_PLACEHOLDERS = {
  ground_transport: '/Images/Full.png',
  security_service: '/Images/Custodia.png',
  road_assistance: '/Images/Grua.png',
  suppliers: '/Images/Pallet.png',
  logistics_operator: '/Images/Proyecto.png',
};

const PUBLICATION_KEYWORD_PLACEHOLDERS = [
  { patterns: ['sello', 'sellos'], src: '/Images/Sellos.png' },
  { patterns: ['etiqueta', 'imo'], src: '/Images/EtiquetasIMO.png' },
  { patterns: ['pallet', 'tarima'], src: '/Images/Pallet.png' },
  { patterns: ['embalaje'], src: '/Images/Embalaje.png' },
  { patterns: ['grua'], src: '/Images/Grua.png' },
  { patterns: ['custodia'], src: '/Images/Custodia.png' },
  { patterns: ['proyecto'], src: '/Images/Proyecto.png' },
  { patterns: ['full'], src: '/Images/Full.png' },
  { patterns: ['lowboy'], src: '/Images/Proyecto.png' },
  { patterns: ['caja 53', '53ft', '53 pi', '53 pie'], src: '/Images/Caja53.png' },
  { patterns: ['caja 48', '48ft', '48 pi', '48 pie'], src: '/Images/Caja48.png' },
  { patterns: ['caja 40', '40ft', '40 pi', '40 pie'], src: '/Images/Caja48.png' },
  { patterns: ['3.5', '3 5', '3.5 ton', '3.5ton'], src: '/Images/3.5ton.png' },
  { patterns: ['rabon'], src: '/Images/Rabon.jpg' },
  { patterns: ['plataforma', 'trailer'], src: '/Images/Full.png' },
  { patterns: ['gruista'], src: '/Images/Grua.png' },
];

const DEFAULT_PUBLICATION_PLACEHOLDER = '/Images/Proyecto.png';

const MARKET_SPOTLIGHTS = [
  {
    id: 'veracruz-transport',
    eyebrow: 'Servicio terrestre',
    title: 'Transportes en Veracruz',
    description: 'Unidades listas para mover tu carga desde el puerto.',
    locationParam: 'Veracruz',
    categoryParam: 'servicio terrestre',
    icon: FaTruck,
  },
  {
    id: 'manzanillo-transport',
    eyebrow: 'Servicio terrestre',
    title: 'Transportes en Manzanillo',
    description: 'Cobertura confiable para importaciones y exportaciones.',
    locationParam: 'Manzanillo',
    categoryParam: 'servicio terrestre',
    icon: FaRoute,
  },
  {
    id: 'customs',
    eyebrow: 'Agentes aduanales',
    title: 'Agentes aduanales',
    description: 'Acelera tus pedimentos y liberaciones en frontera o puerto.',
    categoryParam: 'agentes aduanales',
    icon: FaPassport,
  },
  {
    id: 'warehouses',
    eyebrow: 'Almacenaje',
    title: 'Almacenes estrategicos',
    description: 'Espacios seguros para tu inventario nacional o fiscal.',
    categoryParam: 'almacenes',
    icon: FaWarehouse,
  },
  {
    id: 'cdmx-transport',
    eyebrow: 'Servicio terrestre',
    title: 'Transportes en Ciudad de Mexico',
    description: 'Red metropolitana lista para entregas ultima milla.',
    locationParam: 'Ciudad de Mexico',
    categoryParam: 'servicio terrestre',
    icon: FaTruck,
  },
];

function MarketplaceSpotlightCard({ onSelect }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveIndex(previous => (previous + 1) % MARKET_SPOTLIGHTS.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const activeSlide = MARKET_SPOTLIGHTS[activeIndex];
  const slideTitle = (activeSlide.title || '').trim();
  const slideDescription = (activeSlide.description || '').trim();
  const showDescription =
    slideDescription && slideTitle && slideDescription.toLowerCase() !== slideTitle.toLowerCase();
  const Icon = activeSlide.icon || FaTruck;

  return (
    <section
      className="feed-spotlight-card feed-card feed-spotlight-card--hero"
      aria-label="Recomendaciones del marketplace"
    >
      <header className="feed-spotlight-card__head">
        <span>Explora en Flotte</span>
        <div className="feed-spotlight-card__dots" role="tablist">
          {MARKET_SPOTLIGHTS.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`feed-spotlight-card__dot${index === activeIndex ? ' is-active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Ver ${slide.title}`}
              aria-pressed={index === activeIndex}
            />
          ))}
        </div>
      </header>
      <button
        type="button"
        className="feed-spotlight-card__slide"
        onClick={() => onSelect(activeSlide)}
      >
        <span className="feed-spotlight-card__icon" aria-hidden="true">
          <Icon />
        </span>
        <span className="feed-spotlight-card__eyebrow">{activeSlide.eyebrow}</span>
        <strong>{slideTitle}</strong>
        {showDescription ? <p>{slideDescription}</p> : null}
        <div className="feed-spotlight-card__cta feed-spotlight-card__cta--bottom">
          Ver en Marketplace
        </div>
      </button>
    </section>
  );
}

function CompanySpotlightCard({ companies = [], onSelect }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [companies.length]);

  useEffect(() => {
    if (!companies.length) {
      return undefined;
    }
    const interval = setInterval(() => {
      setActiveIndex(previous => (previous + 1) % companies.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [companies]);

  if (!companies.length) {
    return null;
  }

  const safeIndex = Math.min(activeIndex, companies.length - 1);
  const activeCompany = companies[safeIndex];
  const initials = getCompanyInitial(activeCompany.name);

  const companyTagline = activeCompany.tagline || 'Conecta con la comunidad de Flotte.';
  const companyLocation = (activeCompany.location || '').trim();

  return (
    <section className="feed-company-card feed-card" aria-label="Empresas destacadas">
      <span className="feed-company-card__badge">Publicidad</span>
      <div className="feed-company-card__media">
        <button
          type="button"
          className="feed-company-card__visual"
          onClick={() => onSelect(activeCompany)}
          aria-label={`Ver perfil de ${activeCompany.name}`}
        >
          {activeCompany.logoUrl ? (
            <img
              src={activeCompany.logoUrl}
              alt={activeCompany.name}
              className="feed-company-card__image"
            />
          ) : (
            <span className="feed-company-card__image feed-company-card__image--fallback" aria-hidden="true">
              {initials}
            </span>
          )}
          <span className="feed-company-card__gradient" aria-hidden="true" />
          <div className="feed-company-card__details">
            <strong className="feed-company-card__title">{activeCompany.name}</strong>
            {companyLocation ? (
              <span className="feed-company-card__location">
                <FiMapPin aria-hidden="true" />
                {companyLocation}
              </span>
            ) : null}
            <p className="feed-company-card__description">{companyTagline}</p>
          </div>
        </button>
        <div
          className="feed-company-card__indicators"
          role="tablist"
          aria-label="Cambiar empresa destacada"
        >
          {companies.map((company, index) => (
            <button
              key={company.id || company.slug || `company-${index}`}
              type="button"
              className={`feed-company-card__indicator${index === safeIndex ? ' is-active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Ver ${company.name}`}
              aria-pressed={index === safeIndex}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        className="feed-company-card__cta"
        onClick={() => onSelect(activeCompany)}
      >
        Ver empresa
      </button>
    </section>
  );
}

const buildSpotlightCompaniesFromPublications = (publications = []) => {
  if (!Array.isArray(publications) || !publications.length) {
    return [];
  }

  const catalog = new Map();

  publications.forEach(publication => {
    if (!publication) return;
    const slug = String(publication.companySlug || '').trim();
    const companyId = String(publication.companyId || publication.company || '').trim();
    const catalogKey =
      slug ||
      companyId ||
      String(publication.companyName || publication.title || publication.id || '').trim();

    if (!catalogKey || catalog.has(catalogKey)) {
      return;
    }

    const rawTagline =
      publication.companyTagline ||
      stripHtml(publication.description || '') ||
      publication.title ||
      '';
    const membershipPlan = String(publication.companyMembershipPlan || '').trim().toLowerCase();
    const coverage = publication.companyCoverage || publication.location || '';

    catalog.set(catalogKey, {
      id: companyId || catalogKey,
      slug,
      name: publication.companyName || publication.title || 'Empresa',
      logoUrl:
        publication.companyLogoUrl ||
        publication.primaryImageUrl ||
        publication.bannerUrl ||
        getPublicationImage(publication),
      tagline: normalizeSentenceCase(rawTagline) || 'Conecta con la comunidad de Flotte.',
      coverage,
      location: coverage,
      responseTime: publication.companyResponseTime || '',
      membershipPlan: membershipPlan || 'free',
      createdAt: publication.created || '',
      updatedAt: publication.created || '',
    });
  });

  return Array.from(catalog.values());
};

const translateGenericErrorMessage = value => {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'something went wrong.' || lower === 'something went wrong') {
    return 'Ocurrio un problema. Recarga la pagina e intenta de nuevo.';
  }
  if (
    lower === 'something went wrong. intenta de nuevo en unos minutos.' ||
    lower === 'something went wrong intenta de nuevo en unos minutos.' ||
    lower === 'something went wrong.\nintenta de nuevo en unos minutos.'
  ) {
    return 'Ocurrio un problema. Recarga la pagina y vuelve a intentarlo en unos minutos.';
  }
  return trimmed;
};

const withGenericErrorTranslation = (message, fallback = '') =>
  translateGenericErrorMessage(message) || fallback;

const FEED_PAGE_SIZE = 5;
const MAX_FEED_ITEMS = 75;
const FEED_SOURCE_PAGE_SIZE = 5;
const FEED_POST_FETCH_LIMIT = FEED_SOURCE_PAGE_SIZE;
const REQUEST_FETCH_LIMIT = FEED_SOURCE_PAGE_SIZE;
const PUBLICATION_FETCH_LIMIT = FEED_SOURCE_PAGE_SIZE;
const INITIAL_FEED_SKELETON_COUNT = Math.min(6, FEED_PAGE_SIZE);
const REQUEST_ALLOWED_STATUSES = new Set(['published', 'completed', 'completed_ok', 'completed_error']);

const REQUEST_SERVICE_MESSAGES = {
  transport: 'servicio de transporte',
  custodia: 'servicio de custodia',
  logistica: 'servicios logísticos',
};

const mergePageItems = (current = [], incoming = [], { append = true } = {}) => {
  if (!append) {
    return Array.isArray(incoming)
      ? incoming.slice(0, MAX_FEED_ITEMS)
      : [];
  }

  const base = Array.isArray(current) ? [...current] : [];
  if (!Array.isArray(incoming) || !incoming.length) {
    return base;
  }

  incoming.forEach(item => {
    if (!item) return;
    const identifier = item.id || '';
    if (identifier) {
      const existingIndex = base.findIndex(entry => entry && entry.id === identifier);
      if (existingIndex >= 0) {
        base[existingIndex] = item;
      } else {
        base.push(item);
      }
    } else {
      base.push(item);
    }
  });

  if (base.length > MAX_FEED_ITEMS) {
    return base.slice(0, MAX_FEED_ITEMS);
  }

  return base;
};

const resolvePublicationPlaceholder = publication => {
  const text = normalizeText(
    [
      publication.unitName,
      publication.title,
      publication.typeLabel,
      publication.companyCategory,
    ]
      .filter(Boolean)
      .join(' ')
  );

  if (text) {
    for (const entry of PUBLICATION_KEYWORD_PLACEHOLDERS) {
      if (entry.patterns.some(keyword => text.includes(keyword))) {
        return entry.src;
      }
    }
  }

  if (publication.companyCategory) {
    const placeholder = PUBLICATION_CATEGORY_PLACEHOLDERS[publication.companyCategory];
    if (placeholder) {
      return placeholder;
    }
  }

  if (publication.type) {
    const placeholder = PUBLICATION_TYPE_PLACEHOLDERS[publication.type];
    if (placeholder) {
      return placeholder;
    }
  }

  return DEFAULT_PUBLICATION_PLACEHOLDER;
};

const getPublicationImage = publication => {
  const nestedUnitPhoto =
    publication?.unit?.photoUrl ||
    publication?.unit?.photo ||
    (Array.isArray(publication?.unit?.photos) ? publication.unit.photos[0] : '');
  const directSources = [
    publication.primaryImageUrl,
    publication.unitPhotoUrl,
    nestedUnitPhoto,
    publication.productImageUrl,
    publication.bannerUrl,
    publication.companyHeroUrl,
    publication.companyLogoUrl,
  ];
  const direct = directSources.find(Boolean);
  if (direct) {
    return { src: direct, placeholder: false };
  }
  const fallback = resolvePublicationPlaceholder(publication);
  return { src: fallback, placeholder: false };
};



const normalizeCurrencyCode = currency => {
  if (typeof currency === 'string' && currency.trim().length === 3) {
    return currency.trim().toUpperCase();
  }
  return 'MXN';
};

const parseBudgetLabel = (value, currencyHint = 'MXN') => {
  const fallbackCurrency = normalizeCurrencyCode(currencyHint);
  if (value === null || value === undefined || value === '') {
    return 'Presupuesto: por definir';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const locale = fallbackCurrency === 'USD' ? 'en-US' : 'es-MX';
    const formatted = value.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `Presupuesto: ${formatted} ${fallbackCurrency}`;
  }

  const raw = String(value).trim();
  if (!raw) return 'Presupuesto: por definir';

  const normalized = raw.toLowerCase();
  if (normalized === 'consulta precio') return 'Consulta precio';
  if (normalized.startsWith('presupuesto')) return raw;

  let currency = fallbackCurrency;
  if (normalized.includes('usd')) currency = 'USD';
  else if (normalized.includes('mxn')) currency = 'MXN';

  // Attempt to extract a numeric portion from the string.
  const numericPortion = raw.replace(/[^\d.,-]/g, '');
  let parsed = numericPortion;
  if (parsed.includes(',') && parsed.includes('.')) {
    const lastComma = parsed.lastIndexOf(',');
    const lastDot = parsed.lastIndexOf('.');
    if (lastDot > lastComma) {
      parsed = parsed.replace(/,/g, '');
    } else {
      parsed = parsed.replace(/\./g, '').replace(',', '.');
    }
  } else if (parsed.includes(',')) {
    parsed = parsed.replace(/\./g, '').replace(',', '.');
  } else {
    parsed = parsed.replace(/,/g, '');
  }

  const amount = parseFloat(parsed);
  if (Number.isFinite(amount)) {
    const locale = currency === 'USD' ? 'en-US' : 'es-MX';
    const formatted = amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `Presupuesto: ${formatted} ${currency}`;
  }

  return raw;
};

const normalizeCategoryValue = value => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

const NEWS_CATEGORY_KEYS = new Set(['news', 'news_agent', 'news_agency']);
const isNewsCategoryValue = value => {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  return NEWS_CATEGORY_KEYS.has(normalizeCategoryValue(value));
};

const collectCompanyCategories = company => {
  if (!company) return [];
  const values = [];
  const push = raw => {
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) {
        values.push(trimmed);
      }
    }
  };
  if (Array.isArray(company.categories)) {
    company.categories.forEach(push);
  }
  push(company.category);
  push(company.companyCategory);
  push(company.companyType);
  push(company.type);
  return [...new Set(values)];
};

const resolveIsNewsPost = post => {
  if (!post) return false;
  if (post.isNews === true) return true;
  if (isNewsCategoryValue(post.visibility)) return true;
  if (post.company?.isNews) return true;
  const pool = [];
  if (Array.isArray(post.companyCategories)) {
    pool.push(...post.companyCategories);
  }
  if (post.companyCategory) {
    pool.push(post.companyCategory);
  }
  if (post.companyType) {
    pool.push(post.companyType);
  }
  pool.push(...collectCompanyCategories(post.company));
  return pool.some(isNewsCategoryValue);
};

export default function FeedSection({ user, empresaValues, accountCard = null }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [publications, setPublications] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [companyLookup, setCompanyLookup] = useState({});
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingPublications, setLoadingPublications] = useState(false);
  const [loadingFeedPosts, setLoadingFeedPosts] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [publicationError, setPublicationError] = useState('');
  const [feedPostError, setFeedPostError] = useState('');
  const [feedNotification, setFeedNotification] = useState({ type: '', message: '' });
  const [reactionMap, setReactionMap] = useState({});
  const [reactionCounts, setReactionCounts] = useState({});
  const [deletingPostId, setDeletingPostId] = useState('');
  const [confirmingPost, setConfirmingPost] = useState(null);
  const [editingPostId, setEditingPostId] = useState('');
  const [editingPostContent, setEditingPostContent] = useState('');
  const [editingPostSaving, setEditingPostSaving] = useState(false);
  const [editingPostError, setEditingPostError] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleFeedCount, setVisibleFeedCount] = useState(FEED_PAGE_SIZE);
  const [spotlightCompanies, setSpotlightCompanies] = useState([]);
  const [spotlightLoaded, setSpotlightLoaded] = useState(false);
  const [showInitialFeedSkeleton, setShowInitialFeedSkeleton] = useState(true);
  const [feedPostsPage, setFeedPostsPage] = useState(0);
  const [feedPostsHasMore, setFeedPostsHasMore] = useState(true);
  const [requestsPage, setRequestsPage] = useState(0);
  const [requestsHasMore, setRequestsHasMore] = useState(true);
  const [publicationsPage, setPublicationsPage] = useState(0);
  const [publicationsHasMore, setPublicationsHasMore] = useState(true);
  const initialFeedPhasesRef = React.useRef({
    posts: false,
    requests: false,
    publications: false,
  });
  const feedAutoFetchLockRef = React.useRef(false);
  const feedPostsInitialLoadRef = React.useRef(false);
  const requestsInitialLoadRef = React.useRef(false);
  const publicationsInitialLoadRef = React.useRef(false);
  const loadMoreTriggerRef = React.useRef(null);
  const handleInitialFeedPhaseSettled = useCallback(phase => {
    if (!phase) return;
    const tracker = initialFeedPhasesRef.current;
    if (tracker[phase]) {
      return;
    }
    tracker[phase] = true;
    if (tracker.posts && tracker.requests && tracker.publications) {
      setShowInitialFeedSkeleton(false);
    }
  }, []);
  const handleMarketplaceSpotClick = useCallback(
    slide => {
      if (!slide) return;
      const params = new URLSearchParams();
      if (slide.locationParam) {
        params.set('location', slide.locationParam);
      }
      if (slide.categoryParam) {
        params.set('category', slide.categoryParam);
      }
      navigate(`/marketplace?${params.toString()}`);
    },
    [navigate]
  );

  const handleCompanySpotlightClick = useCallback(
    company => {
      if (!company) return;
      if (company.slug) {
        navigate(`/empresa/${company.slug}`);
        return;
      }
      navigate(`/marketplace?company=${encodeURIComponent(company.id)}`);
    },
    [navigate]
  );

  const subscriptionStatus = String(accountCard?.subscriptionStatus || '').trim();
  const subscriptionPlan = String(accountCard?.subscriptionPlan || '').trim().toLowerCase();
  const normalizedStatus = subscriptionStatus.toLowerCase();
  const showSubscriptionCTA =
    !normalizedStatus || normalizedStatus === 'plan sin asignar' || normalizedStatus === 'free';
  const subscriptionHref = showSubscriptionCTA
    ? '/dashboard?section=suscripciones'
    : '/dashboard/suscripciones/beneficios';
  const subscriptionPlanClass = subscriptionPlan
    ? ` sidebar-subscription-card--plan-${subscriptionPlan.replace(/[^a-z0-9]+/g, '-')}`
    : '';
  const showVerificationReminder = Boolean(accountCard?.verificationCTA);
  const verificationHref = accountCard?.verificationHref || '/dashboard?section=empresa-verificacion';
  const shouldRenderPlanCard = Boolean(accountCard);

  const buildAvatarNode = ({ name, avatarUrl, fallbackInitials, profileHref }) => {
    const avatarClass = `feed-card__avatar${avatarUrl ? ' feed-card__avatar--image' : ''}`;
    const content = avatarUrl ? <img src={avatarUrl} alt="" loading="lazy" /> : fallbackInitials;
    if (profileHref) {
      return (
        <button
          type="button"
          className="feed-card__avatar-button"
          aria-label={`Ver perfil de ${name}`}
          onClick={() => navigate(profileHref)}
        >
          <span className={avatarClass} aria-hidden="true">
            {content}
          </span>
        </button>
      );
    }
    return (
      <div className={avatarClass} aria-hidden="true">
        {content}
      </div>
    );
  };

  const pickFirstFilled = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) continue;
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) {
          return trimmed;
        }
        continue;
      }
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return String(candidate);
      }
      if (typeof candidate === 'object' && candidate !== null) {
        const possible =
          candidate.value ||
          candidate.id ||
          candidate.name ||
          candidate.companyId;
        if (typeof possible === 'string' && possible.trim()) {
          return possible.trim();
        }
        if (typeof possible === 'number' && Number.isFinite(possible)) {
          return String(possible);
        }
      }
    }
    return '';
  };

  const normalizeCompanyIdValue = value => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === 'object' && value !== null) {
      if (typeof value.id === 'string' && value.id.trim()) {
        return value.id.trim();
      }
      if (typeof value.id === 'number' && Number.isFinite(value.id)) {
        return String(value.id);
      }
      if (typeof value.companyId === 'string' && value.companyId.trim()) {
        return value.companyId.trim();
      }
      if (typeof value.companyId === 'number' && Number.isFinite(value.companyId)) {
        return String(value.companyId);
      }
    }
    return '';
  };

  const getRequestCompanyId = useCallback(
    request =>
      normalizeCompanyIdValue(
        request?.companyId ||
          request?.company?.id ||
          request?.company?.companyId ||
          request?.company ||
          ''
      ),
    []
  );

  const buildProposalContactDefaults = useCallback(() => {
    const providerName = pickFirstFilled(
      empresaValues?.contactName,
      empresaValues?.name,
      user?.name,
      user?.username,
      user?.email
    );
    const providerEmail = pickFirstFilled(
      user?.email,
      empresaValues?.contactEmail,
      empresaValues?.email,
      empresaValues?.ownerEmail
    );
    const providerPhone = pickFirstFilled(
      user?.phoneNumber,
      user?.phone,
      empresaValues?.phoneNumber,
      empresaValues?.contactPhone
    );
    return {
      providerName,
      providerEmail,
      providerPhone,
    };
  }, [empresaValues, user]);

  const userCompanyIds = useMemo(() => {
    const ids = new Set();
    const pushId = value => {
      if (!value) return;
      const normalized = String(value).trim();
      if (normalized) {
        ids.add(normalized);
      }
    };
    pushId(empresaValues?.id);
    pushId(empresaValues?.companyId);
    pushId(user?.company);
    pushId(user?.companyId);
    if (Array.isArray(user?.companies)) {
      user.companies.forEach(pushId);
    } else if (user?.companies && typeof user.companies === 'string') {
      pushId(user.companies);
    }
    return ids;
  }, [empresaValues?.id, empresaValues?.companyId, user?.company, user?.companyId, user?.companies]);

  const resolveProviderCompanyId = useCallback(() => {
    const candidates = [
      empresaValues?.id,
      empresaValues?.companyId,
      user?.company,
      user?.companyId,
      ...(Array.isArray(user?.companies) ? user.companies : []),
      ...Array.from(userCompanyIds),
    ];
    for (const candidate of candidates) {
      const normalized = normalizeCompanyIdValue(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return '';
  }, [empresaValues, user, userCompanyIds]);

  const [proposalTarget, setProposalTarget] = useState(null);
  const [proposalForm, setProposalForm] = useState(() => ({
    cost: '',
    notes: '',
    ...buildProposalContactDefaults(),
  }));
  const [proposalSubmitting, setProposalSubmitting] = useState(false);
  const [proposalFeedback, setProposalFeedback] = useState({ tone: '', message: '' });

  useEffect(() => {
    const defaults = buildProposalContactDefaults();
    setProposalForm(prev => {
      let changed = false;
      const next = { ...prev };
      if (!prev.providerName?.trim() && defaults.providerName) {
        next.providerName = defaults.providerName;
        changed = true;
      }
      if (!prev.providerEmail?.trim() && defaults.providerEmail) {
        next.providerEmail = defaults.providerEmail;
        changed = true;
      }
      if (!prev.providerPhone?.trim() && defaults.providerPhone) {
        next.providerPhone = defaults.providerPhone;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [buildProposalContactDefaults]);

  const afterFirstBatchRef = React.useRef(null);
  const reportedFeedViewsRef = React.useRef(new Set());
  const { isPromoted } = usePromotionLookup();

  const userRole = useMemo(() => String(user?.role || '').toLowerCase(), [user?.role]);
  const isClientUser = userRole === 'client';
  const currentUserId = user?.id ? String(user.id) : '';

  const isUserCompany = useCallback(
    candidate => {
      if (!candidate) return false;
      const normalized = String(candidate).trim();
      if (!normalized) return false;
      return userCompanyIds.has(normalized);
    },
    [userCompanyIds]
  );

  const isUserRequestOwner = useCallback(
    request => {
      if (!request) return false;
      const creatorId = request.createdBy || request.creator?.id || '';
      if (creatorId && currentUserId && String(creatorId) === currentUserId) {
        return true;
      }
      const requestCompanyId =
        request.companyId ||
        request.company?.id ||
        request.company?.companyId ||
        request.company ||
        '';
      return isUserCompany(requestCompanyId);
    },
    [currentUserId, isUserCompany]
  );

  const isUserPublicationOwner = useCallback(
    publication => {
      if (!publication) return false;
      const creatorId = publication.createdBy || publication.createdByUser?.id || '';
      if (creatorId && currentUserId && String(creatorId) === currentUserId) {
        return true;
      }
      const publicationCompanyId =
        publication.companyId ||
        publication.company?.id ||
        publication.company ||
        '';
      return isUserCompany(publicationCompanyId);
    },
    [currentUserId, isUserCompany]
  );

  const canSendProposal = useCallback(
    request => !isUserRequestOwner(request),
    [isUserRequestOwner]
  );

  const canReservePublication = useCallback(
    publication => {
      if (!isClientUser) return true;
      return !isUserPublicationOwner(publication);
    },
    [isClientUser, isUserPublicationOwner]
  );

  const reactionKeyFor = (kind, id) => `${kind}-${id}`;
  const isReacted = (kind, id) => Boolean(reactionMap[reactionKeyFor(kind, id)]);
  const handleToggleReaction = async (kind, id) => {
    if (!user?.id) {
      setFeedNotification({ type: 'error', message: 'Inicia sesion para reaccionar.' });
      return;
    }

    const key = reactionKeyFor(kind, id);
    const previousReacted = Boolean(reactionMap[key]);
    const previousCount = reactionCounts[key] || 0;
    const optimisticReacted = !previousReacted;
    const optimisticDiff = optimisticReacted ? 1 : -1;
    const optimisticCount = Math.max(0, previousCount + optimisticDiff);

    setReactionMap(prev => {
      const next = { ...prev, [`${key}-anim`]: true };
      if (optimisticReacted) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
    setReactionCounts(prev => ({
      ...prev,
      [key]: optimisticCount,
    }));

    try {
      const result = await toggleReaction(kind, id);
      const resolvedReacted =
        typeof result?.reacted === 'boolean' ? result.reacted : optimisticReacted;
      const fallbackDiff =
        resolvedReacted === previousReacted ? 0 : resolvedReacted ? 1 : -1;
      const serverDiff = typeof result?.diff === 'number' ? result.diff : fallbackDiff;

      setReactionMap(prev => {
        const next = { ...prev, [`${key}-anim`]: true };
        if (resolvedReacted) {
          next[key] = true;
        } else {
          delete next[key];
        }
        return next;
      });
      setReactionCounts(prev => ({
        ...prev,
        [key]: Math.max(0, previousCount + serverDiff),
      }));
    } catch (error) {
      if (error?.code === 'AUTH_REQUIRED') {
        setFeedNotification({ type: 'error', message: 'Inicia sesion para reaccionar.' });
      } else {
        console.warn('No se pudo alternar la reaccion', error);
        setFeedNotification({
          type: 'error',
          message: 'No pudimos registrar tu reaccion. Intenta nuevamente.',
        });
      }
      setReactionMap(prev => {
        const next = { ...prev };
        if (previousReacted) {
          next[key] = true;
        } else {
          delete next[key];
        }
        return next;
      });
      setReactionCounts(prev => ({
        ...prev,
        [key]: Math.max(0, previousCount),
      }));
    } finally {
      setTimeout(() => {
        setReactionMap(prev => {
          const next = { ...prev };
          delete next[`${key}-anim`];
          return next;
        });
      }, 420);
    }
  };

  const resetProposalForm = useCallback(() => {
    setProposalForm({
      cost: '',
      notes: '',
      ...buildProposalContactDefaults(),
    });
    setProposalFeedback({ tone: '', message: '' });
    setProposalSubmitting(false);
  }, [buildProposalContactDefaults]);

  const handleOpenProposal = request => {
    const providerCompanyId = resolveProviderCompanyId();
    const requestCompanyId = getRequestCompanyId(request);

    if (!providerCompanyId) {
      setFeedNotification({
        type: 'error',
        message: 'Asocia tu empresa para enviar propuestas.',
      });
      return;
    }

    if (providerCompanyId && requestCompanyId && providerCompanyId === requestCompanyId) {
      setFeedNotification({
        type: 'error',
        message: 'No puedes enviarte una propuesta a una solicitud de tu empresa.',
      });
      return;
    }

    if (!canSendProposal(request)) {
      setFeedNotification({
        type: 'error',
        message: 'No puedes enviarte una propuesta a una solicitud de tu empresa.',
      });
      return;
    }
    setProposalTarget(request);
    resetProposalForm();
  };

  const handleCloseProposal = () => {
    resetProposalForm();
    setProposalTarget(null);
  };

  const handleProposalFieldChange = event => {
    const { name, value } = event.target;
    setProposalForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitProposal = async event => {
    event.preventDefault();

    if (!proposalTarget) {
      setProposalFeedback({
        tone: 'error',
        message: 'Selecciona una solicitud para enviar tu propuesta.',
      });
      return;
    }

    if (!canSendProposal(proposalTarget)) {
      handleCloseProposal();
      setFeedNotification({
        type: 'error',
        message: 'No puedes enviarte una propuesta a una solicitud de tu empresa.',
      });
      return;
    }

    const trimmedCost = proposalForm.cost.trim();
    if (!trimmedCost) {
      setProposalFeedback({
        tone: 'error',
        message: 'Indica un costo estimado para la propuesta.',
      });
      return;
    }

    const providerName = proposalForm.providerName.trim();
    if (!providerName) {
      setProposalFeedback({
        tone: 'error',
        message: 'Incluye el nombre de la persona de contacto.',
      });
      return;
    }

    const providerEmail = proposalForm.providerEmail.trim();
    if (!providerEmail) {
      setProposalFeedback({
        tone: 'error',
        message: 'Incluye un correo electrónico de contacto.',
      });
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(providerEmail)) {
      setProposalFeedback({
        tone: 'error',
        message: 'El correo electrónico proporcionado no es válido.',
      });
      return;
    }

    const providerPhone = proposalForm.providerPhone.trim();
    if (!providerPhone) {
      setProposalFeedback({
        tone: 'error',
        message: 'Incluye un teléfono de contacto.',
      });
      return;
    }

    const providerCompanyId = resolveProviderCompanyId();
    if (!providerCompanyId) {
      setProposalFeedback({
        tone: 'error',
        message: 'Necesitas asociar tu empresa para enviar propuestas.',
      });
      return;
    }

    const requestCompanyId = getRequestCompanyId(proposalTarget);
    if (providerCompanyId && requestCompanyId && providerCompanyId === requestCompanyId) {
      setProposalFeedback({
        tone: 'error',
        message: 'No puedes enviar propuestas a solicitudes publicadas por tu empresa.',
      });
      return;
    }

    setProposalSubmitting(true);
    setProposalFeedback({ tone: '', message: '' });

    try {
      const notes = (proposalForm.notes || '').trim();

      const payload = {
        requestId: proposalTarget.id,
        companyId: providerCompanyId,
        createdBy: user?.id,
        price: trimmedCost,
        details: notes,
        providerName,
        providerEmail,
        providerPhone,
      };

      const savedProposal = await createRequestProposal(payload);

      const requestTitle = proposalTarget.title || 'tu solicitud';
      const requesterCompanyId =
        normalizeCompanyIdValue(proposalTarget.company?.id) ||
        normalizeCompanyIdValue(proposalTarget.companyId);
      const providerDisplayName = pickFirstFilled(
        empresaValues?.name,
        providerName
      );

      const proposalUrl = `/dashboard?section=solicitudes&request=${encodeURIComponent(
        proposalTarget.id
      )}&intent=view_request_proposals${
        savedProposal?.id ? `&proposal=${encodeURIComponent(savedProposal.id)}` : ''
      }`;

      if (requesterCompanyId && requesterCompanyId !== providerCompanyId) {
        try {
          await createNotification({
            company: requesterCompanyId,
            type: 'request_proposal',
            title: `Recibiste una propuesta de ${providerDisplayName || 'un proveedor'}`,
            message: `La empresa ${providerDisplayName || 'un proveedor de Flotte'} envió una propuesta para ${requestTitle}. Monto estimado: ${trimmedCost}.`,
            destinationUrl: proposalUrl,
            data: {
              requestId: proposalTarget.id,
              proposalId: savedProposal?.id || '',
              providerCompanyId,
              providerName,
              providerEmail,
              providerPhone,
              price: trimmedCost,
            },
          });
        } catch (notifyError) {
          console.warn('No se pudo notificar al cliente de la nueva propuesta.', notifyError);
        }
      }

      setFeedNotification({
        type: 'success',
        message: 'Tu propuesta fue enviada al solicitante.',
      });
      handleCloseProposal();
    } catch (submitError) {
      console.error('No se pudo enviar la propuesta.', submitError);
      setProposalFeedback({
        tone: 'error',
        message: submitError?.message || 'No pudimos enviar la propuesta. Intenta nuevamente.',
      });
    } finally {
      setProposalSubmitting(false);
    }
  };

  const loadFeedPostsPage = useCallback(
    async (pageToLoad = 1, { reset = pageToLoad === 1, force = false } = {}) => {
      if (!force && !reset && !feedPostsHasMore) {
        return;
      }

      setLoadingFeedPosts(true);
      if (reset) {
        setFeedPostError('');
      }

      try {
        const response = await listFeedPosts({
          limit: FEED_POST_FETCH_LIMIT,
          page: pageToLoad,
          withMeta: true,
        });
        const incoming = Array.isArray(response?.items) ? response.items : [];
        let nextLength = 0;
        setFeedPosts(prev => {
          const source = reset ? [] : prev;
          const nextItems = mergePageItems(source, incoming, { append: !reset });
          nextLength = nextItems.length;
          return nextItems;
        });
        setFeedPostsPage(response?.page || pageToLoad);
        const canContinue = Boolean(response?.hasMore) && nextLength < MAX_FEED_ITEMS;
        setFeedPostsHasMore(canContinue);
      } catch (err) {
        if (reset) {
          setFeedPostError(
            withGenericErrorTranslation(
              err?.message,
              'No pudimos cargar las publicaciones de la red.'
            )
          );
        } else {
          console.warn('No se pudieron cargar mas publicaciones del feed.', err);
          setFeedNotification(prev => {
            if (prev.message) return prev;
            return {
              type: 'error',
              message: 'No pudimos cargar más posts del feed.',
            };
          });
        }
      } finally {
        setLoadingFeedPosts(false);
        handleInitialFeedPhaseSettled('posts');
      }
    },
    [feedPostsHasMore, handleInitialFeedPhaseSettled]
  );

  const loadRequestPage = useCallback(
    async (pageToLoad = 1, { reset = pageToLoad === 1, force = false } = {}) => {
      if (!force && !reset && !requestsHasMore) {
        return;
      }

      setLoadingRequests(true);
      if (reset) {
        setRequestError('');
      }

      try {
        const response = await listPublishedRequests({
          limit: REQUEST_FETCH_LIMIT,
          page: pageToLoad,
          withMeta: true,
        });
        const normalized = (Array.isArray(response?.items) ? response.items : []).filter(
          item => REQUEST_ALLOWED_STATUSES.has((item?.status || '').toLowerCase())
        );
        let nextLength = 0;
        setRequests(prev => {
          const source = reset ? [] : prev;
          const nextItems = mergePageItems(source, normalized, { append: !reset });
          nextLength = nextItems.length;
          return nextItems;
        });
        setRequestsPage(response?.page || pageToLoad);
        setRequestsHasMore(Boolean(response?.hasMore) && nextLength < MAX_FEED_ITEMS);
      } catch (err) {
        if (reset) {
          setRequestError(
            withGenericErrorTranslation(
              err?.message,
              'No pudimos cargar los requerimientos recientes.'
            )
          );
        } else {
          console.warn('No se pudieron cargar mas requerimientos.', err);
        }
      } finally {
        setLoadingRequests(false);
        handleInitialFeedPhaseSettled('requests');
      }
    },
    [requestsHasMore, handleInitialFeedPhaseSettled]
  );

  const loadPublicationPage = useCallback(
    async (pageToLoad = 1, { reset = pageToLoad === 1, force = false } = {}) => {
      if (!force && !reset && !publicationsHasMore) {
        return;
      }

      setLoadingPublications(true);
      if (reset) {
        setPublicationError('');
      }

      try {
        const response = await listPublishedPublications({
          limit: PUBLICATION_FETCH_LIMIT,
          page: pageToLoad,
          withMeta: true,
        });
        const filtered = (Array.isArray(response?.items) ? response.items : []).filter(
          item =>
            item?.status === 'published' &&
            (!item?.companyCategory || PROVIDER_CATEGORIES.has(item.companyCategory))
        );
        let nextLength = 0;
        setPublications(prev => {
          const source = reset ? [] : prev;
          const nextItems = mergePageItems(source, filtered, { append: !reset });
          nextLength = nextItems.length;
          return nextItems;
        });
        setPublicationsPage(response?.page || pageToLoad);
        setPublicationsHasMore(Boolean(response?.hasMore) && nextLength < MAX_FEED_ITEMS);
      } catch (err) {
        if (reset) {
          setPublicationError(
            err?.message || 'No pudimos cargar las publicaciones recientes.'
          );
        } else {
          console.warn('No se pudieron cargar mas publicaciones.', err);
        }
      } finally {
        setLoadingPublications(false);
        handleInitialFeedPhaseSettled('publications');
      }
    },
    [publicationsHasMore, handleInitialFeedPhaseSettled]
  );

  const handleScrollTopClick = async () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    await loadPublicationPage(1, { reset: true, force: true });
  };

  const handleLoadMoreFeedItems = useCallback(() => {
    setVisibleFeedCount(prev => Math.min(prev + FEED_PAGE_SIZE, MAX_FEED_ITEMS));
  }, []);

  const handleOpenCompanyProfile = useCallback(
    company => {
      if (!company) return;
      const slug = typeof company.slug === 'string' && company.slug.trim();
      const id = typeof company.id === 'string' && company.id.trim();
      const target = slug ? `/empresa/${encodeURIComponent(slug)}` : id ? `/empresa/${encodeURIComponent(id)}` : '';
      if (target) {
        navigate(target);
      }
    },
    [navigate]
  );

  const handleViewAllNews = useCallback(() => {
    navigate('/noticias');
  }, [navigate]);

  useEffect(() => {
    if (feedPostsInitialLoadRef.current) return;
    feedPostsInitialLoadRef.current = true;
    loadFeedPostsPage(1, { reset: true, force: true });
  }, [loadFeedPostsPage]);

  useEffect(() => {
    if (requestsInitialLoadRef.current) return;
    requestsInitialLoadRef.current = true;
    loadRequestPage(1, { reset: true, force: true });
  }, [loadRequestPage]);

  useEffect(() => {
    if (publicationsInitialLoadRef.current) return;
    publicationsInitialLoadRef.current = true;
    loadPublicationPage(1, { reset: true, force: true });
  }, [loadPublicationPage]);

  useEffect(() => {
    const missingIds = publications
      .map(item => item.companyId)
      .filter(id => id && !companyLookup[id]);
    const uniqueIds = [...new Set(missingIds)];
    if (!uniqueIds.length) return;

    let ignore = false;
    const loadCompanies = async () => {
      const entries = await Promise.all(
        uniqueIds.map(async id => {
          try {
            const company = await getCompanyById(id);
            return [
              id,
              {
                name: company?.name || 'Proveedor',
                logoUrl: company?.logoUrl || '',
                slug: company?.slug || '',
              },
            ];
          } catch {
            return [
              id,
              {
                name: 'Proveedor',
                logoUrl: '',
                slug: '',
              },
            ];
          }
        })
      );
      if (!ignore) {
        setCompanyLookup(prev => {
          const next = { ...prev };
          entries.forEach(([id, info]) => {
            next[id] = info;
          });
          return next;
        });
      }
    };

    loadCompanies();
    return () => {
      ignore = true;
    };
  }, [publications, companyLookup]);

  useEffect(() => {
    let ignore = false;
    listPublishedCompanies({ limit: 120 })
      .then(list => {
        if (ignore) return;
        const normalized = Array.isArray(list) ? list.filter(Boolean) : [];
        setSpotlightCompanies(normalized);
        setSpotlightLoaded(true);
      })
      .catch(() => {
        if (ignore) return;
        setSpotlightCompanies([]);
        setSpotlightLoaded(true);
      });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!spotlightLoaded || spotlightCompanies.length || !publications.length) {
      return;
    }
    setSpotlightCompanies(buildSpotlightCompaniesFromPublications(publications));
  }, [spotlightLoaded, spotlightCompanies, publications]);

  const compareByCreationDesc = useCallback(
    (a, b) => toTimestamp(resolveCreatedAt(b)) - toTimestamp(resolveCreatedAt(a)),
    []
  );

  const requestItems = useMemo(() => {
    return requests
      .map(buildRequestFeedItem)
      .sort(compareByCreationDesc)
      .slice(0, MAX_FEED_ITEMS);
  }, [requests, compareByCreationDesc]);

  const publicationItems = useMemo(() => {
    return publications
      .map(buildPublicationFeedItem)
      .sort(compareByCreationDesc)
      .slice(0, MAX_FEED_ITEMS);
  }, [publications, compareByCreationDesc]);

  const feedPostItems = useMemo(() => {
    return [...feedPosts]
      .filter(post => !resolveIsNewsPost(post))
      .sort(compareByCreationDesc)
      .slice(0, MAX_FEED_ITEMS);
  }, [feedPosts, compareByCreationDesc]);

  const newsPostItems = useMemo(() => {
    return [...feedPosts]
      .filter(resolveIsNewsPost)
      .sort(compareByCreationDesc)
      .slice(0, 12);
  }, [feedPosts, compareByCreationDesc]);

  const companySpotlightItems = useMemo(() => {
    if (!spotlightCompanies.length) {
      return [];
    }

    const planOrder = ['elite', 'premium', 'pro'];
    for (const plan of planOrder) {
      const matches = spotlightCompanies.filter(
        company => String(company.membershipPlan || '').toLowerCase() === plan
      );
      if (matches.length) {
        return matches.slice(0, 5);
      }
    }

    return [...spotlightCompanies].sort(compareByCreationDesc).slice(0, 5);
  }, [spotlightCompanies, compareByCreationDesc]);

  const combinedFeedItems = useMemo(() => {
    const merged = [];

    feedPostItems.forEach(post => {
      const linkedRequestId = post.attachedRequestId || post.attachedRequest?.id;
      if (linkedRequestId) {
        return;
      }
      merged.push({
        key: `post-${post.id}`,
        type: 'post',
        createdAt: resolveCreatedAt(post),
        payload: post,
      });
    });

    requestItems.forEach(item => {
      merged.push({
        key: `request-${item.id}`,
        type: 'request',
        createdAt: resolveCreatedAt(item),
        payload: item,
      });
    });

    publicationItems.forEach(item => {
      merged.push({
        key: `publication-${item.id}`,
        type: 'publication',
        createdAt: resolveCreatedAt(item),
        payload: item,
      });
    });

    return merged.sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
  }, [feedPostItems, requestItems, publicationItems]);

  const combinedFeedCount = combinedFeedItems.length;
  const combinedFeedCountRef = React.useRef(combinedFeedCount);

  useEffect(() => {
    combinedFeedCountRef.current = combinedFeedCount;
  }, [combinedFeedCount]);

  const ensureFeedCapacity = useCallback(() => {
    if (
      !feedPostsInitialLoadRef.current ||
      !requestsInitialLoadRef.current ||
      !publicationsInitialLoadRef.current
    ) {
      return;
    }
    const desired = Math.min(visibleFeedCount + FEED_PAGE_SIZE, MAX_FEED_ITEMS);
    if (combinedFeedCount >= desired) {
      return;
    }
    if (feedAutoFetchLockRef.current) {
      return;
    }

    const loaders = [];
    if (feedPostsHasMore && !loadingFeedPosts) {
      loaders.push(() => loadFeedPostsPage(feedPostsPage + 1));
    }
    if (requestsHasMore && !loadingRequests) {
      loaders.push(() => loadRequestPage(requestsPage + 1));
    }
    if (publicationsHasMore && !loadingPublications) {
      loaders.push(() => loadPublicationPage(publicationsPage + 1));
    }

    if (!loaders.length) {
      return;
    }

    feedAutoFetchLockRef.current = true;
    const runSequentialLoaders = async () => {
      for (const nextLoader of loaders) {
        try {
          await nextLoader();
        } catch (loaderError) {
          console.warn('No se pudo cargar una de las fuentes del feed.', loaderError);
        }
        if (combinedFeedCountRef.current >= desired) {
          break;
        }
      }
    };

    runSequentialLoaders()
      .catch(() => {})
      .finally(() => {
        feedAutoFetchLockRef.current = false;
      });
  }, [
    combinedFeedCount,
    visibleFeedCount,
    feedPostsHasMore,
    loadingFeedPosts,
    loadFeedPostsPage,
    feedPostsPage,
    requestsHasMore,
    loadingRequests,
    loadRequestPage,
    requestsPage,
    publicationsHasMore,
    loadingPublications,
    loadPublicationPage,
    publicationsPage,
  ]);

  useEffect(() => {
    ensureFeedCapacity();
  }, [ensureFeedCapacity]);

  useEffect(() => {
    setVisibleFeedCount(prev => {
      const available = Math.min(MAX_FEED_ITEMS, combinedFeedCount || FEED_PAGE_SIZE);
      if (!prev) return available;
      return Math.min(prev, available);
    });
  }, [combinedFeedCount]);

  useEffect(() => {
    const postIds = feedPostItems
      .filter(item => !(item.attachedRequestId || item.attachedRequest?.id))
      .map(item => item.id)
      .filter(Boolean);
    const requestIds = requestItems.map(item => item.id).filter(Boolean);
    const publicationIds = publicationItems.map(item => item.id).filter(Boolean);

    if (!postIds.length && !requestIds.length && !publicationIds.length) {
      return;
    }

    let ignore = false;

    const loadReactionStats = async () => {
      try {
        const [postStats, requestStats, publicationStats] = await Promise.all([
          postIds.length ? fetchReactionStats('post', postIds) : Promise.resolve({ counts: {}, userMap: {} }),
          requestIds.length ? fetchReactionStats('request', requestIds) : Promise.resolve({ counts: {}, userMap: {} }),
          publicationIds.length ? fetchReactionStats('publication', publicationIds) : Promise.resolve({ counts: {}, userMap: {} }),
        ]);

        if (ignore) return;

        const nextCounts = {};
        const nextMap = {};

        const assignStats = (kind, ids, stats) => {
          ids.forEach(id => {
            const key = reactionKeyFor(kind, id);
            nextCounts[key] = stats.counts?.[id] || 0;
            nextMap[key] = Boolean(stats.userMap?.[id]);
          });
        };

        assignStats('post', postIds, postStats);
        assignStats('request', requestIds, requestStats);
        assignStats('publication', publicationIds, publicationStats);

        setReactionCounts(prev => ({ ...prev, ...nextCounts }));
        setReactionMap(prev => ({ ...prev, ...nextMap }));
      } catch (error) {
        console.warn('No se pudieron cargar los conteos de reacciones', error);
      }
    };

    loadReactionStats();

    return () => {
      ignore = true;
    };
  }, [feedPostItems, requestItems, publicationItems]);

  const combinedLoading = loadingRequests || loadingPublications || loadingFeedPosts;
  const combinedError = feedPostError || requestError || publicationError;
  const anySourceHasMore = feedPostsHasMore || requestsHasMore || publicationsHasMore;

  const { visibleFeedItems, canLoadMoreFeed, reachedFeedLimit } = useMemo(() => {
    const total = Math.min(MAX_FEED_ITEMS, combinedFeedCount);
    if (!total) {
      return {
        visibleFeedItems: [],
        canLoadMoreFeed: anySourceHasMore,
        reachedFeedLimit: false,
      };
    }

    const effective = Math.min(Math.max(visibleFeedCount, FEED_PAGE_SIZE), total);
    const actualVisible = Math.min(effective, combinedFeedItems.length);
    const items = combinedFeedItems.slice(0, actualVisible);
    const canLoad = actualVisible < total || anySourceHasMore;
    const reachedLimit = !canLoad && !anySourceHasMore && total >= MAX_FEED_ITEMS;

    return {
      visibleFeedItems: items,
      canLoadMoreFeed: canLoad,
      reachedFeedLimit: reachedLimit,
    };
  }, [combinedFeedItems, combinedFeedCount, visibleFeedCount, anySourceHasMore]);

  useEffect(() => {
    const target = loadMoreTriggerRef.current;
    if (!target || !canLoadMoreFeed) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      handleLoadMoreFeedItems();
      return;
    }

    let cancelled = false;
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting || cancelled) {
        return;
      }
      observer.unobserve(entry.target);
      handleLoadMoreFeedItems();
    }, { rootMargin: '160px 0px 160px 0px', threshold: 0.1 });

    observer.observe(target);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [canLoadMoreFeed, handleLoadMoreFeedItems, visibleFeedItems.length]);

  useEffect(() => {
    if (!visibleFeedItems.length) {
      return;
    }
    const reported = reportedFeedViewsRef.current;
    const publicationIds = visibleFeedItems
      .filter(item => item && item.type === 'publication' && item.payload?.id)
      .map(item => item.payload.id)
      .filter(id => !reported.has(id));
    if (!publicationIds.length) {
      return;
    }
    publicationIds.forEach(id => reported.add(id));
    publicationIds.forEach(id => {
      registerPublicationFeedView({ id }).catch(error => {
        console.warn('No se pudo registrar la vista desde el feed para la publicación.', error);
        reported.delete(id);
      });
    });
  }, [visibleFeedItems]);

  useEffect(() => {
    if (visibleFeedItems.length < FEED_PAGE_SIZE) {
      setShowScrollTop(false);
      return;
    }

    const sentinel = afterFirstBatchRef.current;
    if (!sentinel) {
      setShowScrollTop(false);
      return;
    }

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      setShowScrollTop(true);
      return;
    }

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        setShowScrollTop(false);
        return;
      }
      const scrolledPast = entry.boundingClientRect.top < 0;
      setShowScrollTop(scrolledPast);
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [visibleFeedItems.length]);

  function renderFeedPostCard(post) {
    if (!post) return null;
    const hasCompany = Boolean(post.company?.name);
    const companySlug = (post.company?.slug || '').trim();
    const authorUsername = (post.author?.username || '').trim();
    const authorId = (post.author?.id || '').trim();
    const displayName = hasCompany
      ? post.company.name
      : post.author?.name || post.author?.email || 'Miembro de la red';
    const subtitle = hasCompany
      ? 'Publicacion de la empresa'
      : 'Publicacion personal';
    const initials = getCompanyInitial(displayName);
    const avatarSrc =
      (hasCompany && post.company?.logoUrl) ||
      post.author?.avatarUrl ||
      '';
    const profileHref = hasCompany
      ? companySlug
        ? `/empresa/${encodeURIComponent(companySlug)}`
        : ''
      : authorUsername || authorId
      ? `/usuario/${encodeURIComponent(authorUsername || authorId)}`
      : '';
    const avatarNode = buildAvatarNode({
      name: displayName,
      avatarUrl: avatarSrc,
      fallbackInitials: initials,
      profileHref,
    });
    const createdAt = resolveCreatedAt(post);
    const timestamp = formatRelativeTime(createdAt);
    const hasRequest = Boolean(post.attachedRequest?.id);
    const canDelete = user?.id && user.id === post.createdBy;
    const isEditing = editingPostId === post.id;
    const reactionKey = reactionKeyFor('post', post.id);
    const reacted = isReacted('post', post.id);
    const reactionTotal = reactionCounts[reactionKey] || 0;

    const normalizedContent = normalizeSentenceCase(post.content || '');
    const displayContent =
      normalizedContent || (typeof post.content === 'string' ? post.content : '');

    const headerActions = canDelete ? (
      <>
        <button
          type="button"
          className="feed-card__action feed-card__action--icon feed-card__edit-btn"
          onClick={() => handleToggleEditPost(post)}
          title={isEditing ? 'Cancelar edicion' : 'Editar publicacion'}
          disabled={editingPostSaving && isEditing}
        >
          {isEditing ? <FaTimes aria-hidden="true" /> : <FaRegEdit aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="feed-card__action feed-card__action--icon"
          onClick={() => handleFeedPostDeleted(post.id)}
          title="Eliminar publicacion"
          disabled={deletingPostId === post.id || isEditing}
        >
          <FaTrash aria-hidden="true" />
        </button>
      </>
    ) : null;

    const postPrimaryAction = profileHref
      ? () => navigate(profileHref)
      : hasRequest
        ? () => navigate(`/solicitudes/${post.attachedRequest.id}`)
        : undefined;

    return (
      <article key={post.id} className="feed-card feed-card--shell feed-card--post">
        {renderCardHeader({
          avatarNode,
          name: displayName,
          subtitle,
          timestampLabel: timestamp ? `Publicada ${timestamp}` : '',
          timestampDate: createdAt,
          typeLabel: 'Post',
          statusLabel: hasRequest ? 'Con solicitud' : null,
          actions: headerActions,
        })}
        <div className="feed-card__shell-body feed-card__shell-body--post">
        {isEditing ? (
          <form
            className="feed-card__edit-form"
            onSubmit={event => handleSaveEditedPost(event, post)}
          >
            <label className="feed-card__edit-field" htmlFor={`feed-edit-${post.id}`}>
              <span className="visually-hidden">Editar publicacion</span>
              <textarea
                id={`feed-edit-${post.id}`}
                name="content"
                value={editingPostContent}
                onChange={handleEditPostChange}
                disabled={editingPostSaving}
                rows={4}
              />
            </label>
            {editingPostError ? (
              <div className="feed-card__edit-error" role="alert">
                {editingPostError}
              </div>
            ) : null}
            <div className="feed-card__edit-actions">
              <button
                type="button"
                className="feed-card__edit-cancel"
                onClick={handleCancelEditPost}
                disabled={editingPostSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="feed-card__edit-save"
                disabled={editingPostSaving}
              >
                {editingPostSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        ) : (
          <p className="feed-card__post-content">
            {displayContent}
          </p>
        )}

        {hasRequest ? (
          <div className="feed-card__attached-request">
            <span className="feed-card__detail feed-card__detail--tag">Solicitud vinculada</span>
            <span className="feed-card__detail">{post.attachedRequest.title}</span>
          </div>
        ) : null}
        </div>
        {renderCardFooter({
          reacted,
          reactionTotal,
          onToggleReaction: () => handleToggleReaction('post', post.id),
          onComment: hasRequest ? () => navigate(`/solicitudes/${post.attachedRequest.id}`) : undefined,
          primaryLabel: 'Ver más',
          onPrimaryAction: postPrimaryAction,
          primaryDisabled: false,
        })}
      </article>
    );
  }

  const userCanEditPost = record => {
    if (!user?.id || !record) return false;
    const ownerId =
      record.createdBy ||
      record.ownerId ||
      record.creator?.id ||
      record.author?.id ||
      record.company?.createdBy ||
      record.company?.ownerId;
    if (ownerId && String(ownerId) === String(user.id)) {
      return true;
    }
    const companyOwner =
      record.companyId ||
      record.company?.id ||
      record.company?.companyId;
    if (empresaValues?.id && companyOwner && String(companyOwner) === String(empresaValues.id)) {
      return true;
    }
    return false;
  };

  function renderRequestCard(item) {
    const canEdit = userCanEditPost(item);
    const isEditing = editingPostId === item.id;
    const reactionKey = reactionKeyFor('request', item.id);
    const reacted = isReacted('request', item.id);
    const reactionTotal = reactionCounts[reactionKey] || 0;
    const hasCompany = Boolean(item.company?.name);
    const companyLogo = item.company?.logoUrl || '';
    const creatorAvatar = item.creator?.avatarUrl || '';
    const avatarSrc = hasCompany ? companyLogo : creatorAvatar;
    const companySlug = (item.company?.slug || '').trim();
    const creatorUsername = (item.creator?.username || '').trim();
    const creatorId = (item.creator?.id || '').trim();
    const displayName = hasCompany
      ? item.company.name
      : item.creator?.name || item.creator?.email || 'Miembro de la red';
    const initials = getCompanyInitial(displayName);
    const avatarClassName = `feed-card__avatar${avatarSrc ? ' feed-card__avatar--image' : ''}`;
    const profileHref = hasCompany
      ? companySlug
        ? `/empresa/${encodeURIComponent(companySlug)}`
        : ''
      : creatorUsername || creatorId
      ? `/usuario/${encodeURIComponent(creatorUsername || creatorId)}`
      : '';
    const avatarContent = avatarSrc ? <img src={avatarSrc} alt="" loading="lazy" /> : initials;
    const avatarNode = profileHref ? (
      <button
        type="button"
        className="feed-card__avatar-button"
        aria-label={`Ver perfil de ${displayName}`}
        title={`Ver perfil de ${displayName}`}
        onClick={() => navigate(profileHref)}
      >
        <span className={avatarClassName} aria-hidden="true">
          {avatarContent}
        </span>
      </button>
    ) : (
      <div className={avatarClassName} aria-hidden="true">
        {avatarContent}
      </div>
    );
    const serviceKey = (item.serviceType || '').toLowerCase();
    const serviceDescriptor =
      REQUEST_SERVICE_MESSAGES[serviceKey] ||
      (item.serviceTypeLabel
        ? `servicio de ${item.serviceTypeLabel.toLocaleLowerCase('es-MX')}`
        : 'un servicio');
    const serviceMessage = `Está buscando ${serviceDescriptor}`;
    const createdAt = resolveCreatedAt(item);
    const timestamp = formatRelativeTime(createdAt);
    const normalizedDetails = normalizeSentenceCase(item.details || '');
    const displayedDetails =
      normalizedDetails || (typeof item.details === 'string' ? item.details : '');
    const allowProposal = canSendProposal(item);
    const requestCardClasses = ['feed-card', 'feed-card--shell', 'feed-card--request'];
    const expired = isRequestExpired(item);
    if (expired) requestCardClasses.push('feed-card--expired');
    const budgetLabel = parseBudgetLabel(item.budget, item.currency);

    const headerActions = canEdit ? (
      <>
        <button
          type="button"
          className="feed-card__action feed-card__action--icon feed-card__edit-btn"
          onClick={() => handleToggleEditPost(item, 'request')}
          title={isEditing ? 'Cancelar edicion' : 'Editar solicitud'}
          disabled={editingPostSaving && isEditing}
        >
          {isEditing ? <FaTimes aria-hidden="true" /> : <FaRegEdit aria-hidden="true" />}
        </button>
      </>
    ) : null;

    return (
      <article key={item.id} className={requestCardClasses.join(' ')}>
        {renderCardHeader({
          avatarNode,
          name: displayName,
          subtitle: serviceMessage,
          timestampLabel: timestamp ? `Publicada ${timestamp}` : '',
          timestampDate: createdAt,
          typeLabel: 'Solicitud',
          statusLabel: expired ? 'Expirada' : null,
          actions: headerActions,
        })}
        <div className="feed-card__shell-body feed-card__shell-body--request">
        {isEditing ? (
          <form
            className="feed-card__edit-form"
            onSubmit={event => handleSaveEditedPost(event, item, 'request')}
          >
            <label className="feed-card__edit-field" htmlFor={`feed-request-edit-${item.id}`}>
              <span className="visually-hidden">Editar solicitud</span>
              <textarea
                id={`feed-request-edit-${item.id}`}
                name="content"
                value={editingPostContent}
                onChange={handleEditPostChange}
                disabled={editingPostSaving}
                rows={4}
              />
            </label>
            {editingPostError ? (
              <div className="feed-card__edit-error" role="alert">
                {editingPostError}
              </div>
            ) : null}
            <div className="feed-card__edit-actions">
              <button
                type="button"
                className="feed-card__edit-cancel"
                onClick={handleCancelEditPost}
                disabled={editingPostSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="feed-card__edit-save"
                disabled={editingPostSaving}
              >
                {editingPostSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="request-card__route">
              <div className="request-card__location-block">
                <span className="request-card__label">Origen</span>
                <strong>{item.pickupLocation || 'Por definir'}</strong>
              </div>
              <div className="request-card__arrow" aria-hidden="true">
                <FaArrowRight />
              </div>
              <div className="request-card__location-block">
                <span className="request-card__label">Destino</span>
                <strong>{item.dropoffLocation || 'Por definir'}</strong>
              </div>
            </div>
            {item.details ? (
              <p className="request-card__details">{displayedDetails}</p>
            ) : null}
          </>
        )}
        </div>
        {renderCardFooter({
          priceLabel: budgetLabel,
          priceEyebrow: 'Presupuesto',
          reacted,
          reactionTotal,
          onToggleReaction: () => handleToggleReaction('request', item.id),
          onComment: () => navigate(`/solicitudes/${item.id}`),
          primaryLabel: allowProposal ? 'Enviar propuesta' : 'Ver solicitud',
          onPrimaryAction: allowProposal ? () => handleOpenProposal(item) : () => navigate(`/solicitudes/${item.id}`),
          primaryDisabled: expired,
        })}
      </article>
    );
  }

  function renderPublicationCard(item) {
    const lookupEntry = companyLookup[item.companyId];
    const lookupName =
      typeof lookupEntry === 'string' ? lookupEntry : lookupEntry?.name;
    const lookupSlug =
      lookupEntry && typeof lookupEntry === 'object' ? lookupEntry.slug : '';
    const companyName = lookupName || item.companyName || 'Proveedor';
    const companySlug = (item.companySlug || lookupSlug || '').trim();
    const { src: imageSrc, placeholder: isPlaceholder } = getPublicationImage({
      ...item,
      companyName,
    });
    const profileHref = companySlug ? `/empresa/${encodeURIComponent(companySlug)}` : '';
    const promoted = isPromoted('publication', item.id);
    const createdAt = resolveCreatedAt(item);
    const relativeTime = createdAt ? formatRelativeTime(createdAt) : '';
    const locationLabel = typeof item.location === 'string' ? item.location.trim() : '';
    const publicationHref = `/publicaciones/${item.numericId || item.id}`;
    const rawPrice = item.price ?? '';
    const priceString =
      typeof rawPrice === 'number'
        ? `${rawPrice}`
        : String(rawPrice || '').trim();
    const priceLabelNormalized = priceString.toLowerCase();
    const priceInquiryOnly =
      !priceLabelNormalized ||
      priceLabelNormalized.startsWith('consulta') ||
      priceLabelNormalized.startsWith('consultar');
    const priceEyebrow = priceInquiryOnly ? 'Consulta precio' : 'Tarifa estimada';
    const priceDisplay = priceInquiryOnly ? 'Consultar' : formatPriceValue(rawPrice);
    const reactionKey = reactionKeyFor('publication', item.id);
    const reacted = isReacted('publication', item.id);
    const reactionTotal = reactionCounts[reactionKey] || 0;
    const excerptCandidate =
      [item.excerpt, item.summary, typeof item.description === 'string' ? item.description.replace(/<[^>]+>/g, ' ') : '']
        .find(value => typeof value === 'string' && value.trim()) || '';
    const excerptSource = typeof excerptCandidate === 'string' ? excerptCandidate.trim() : '';
    const normalizedExcerpt = normalizeSentenceCase(excerptSource);
    const displayedExcerpt = normalizedExcerpt || excerptSource;
    const hasExcerpt = Boolean(displayedExcerpt.trim());
    const canEdit =
      userCanEditPost(item) ||
      (empresaValues?.id && String(empresaValues.id) === String(item.companyId || item.company?.id));
    const isEditing = editingPostId === item.id;
    const allowReservation = canReservePublication(item);

    const articleClassNames = ['feed-card', 'feed-card--shell', 'feed-card--publication'];
    if (promoted) articleClassNames.push('feed-card--promoted');

    const statusValue = String(item.status || '').toLowerCase();
    const statusLabel =
      statusValue === 'published'
        ? 'Disponible'
        : statusValue === 'draft'
          ? 'Borrador'
          : 'Actualizado';
    const statusClasses = ['logistics-card__status'];
    if (statusValue === 'published') statusClasses.push('logistics-card__status--available');
    else if (statusValue === 'draft') statusClasses.push('logistics-card__status--draft');
    else statusClasses.push('logistics-card__status--default');

    const visualClassNames = ['logistics-card__visual'];
    if (isPlaceholder) visualClassNames.push('logistics-card__visual--placeholder');

    const avatarNode = buildAvatarNode({
      name: companyName,
      avatarUrl: item.company?.logoUrl || '',
      fallbackInitials: getCompanyInitial(companyName),
      profileHref,
    });

    const headerActions = canEdit ? (
      <>
        <button
          type="button"
          className="feed-card__action feed-card__action--icon feed-card__edit-btn"
          onClick={() => handleToggleEditPost(item, 'publication')}
          title={isEditing ? 'Cancelar edicion' : 'Editar publicacion'}
          disabled={editingPostSaving && isEditing}
        >
          {isEditing ? <FaTimes aria-hidden="true" /> : <FaRegEdit aria-hidden="true" />}
        </button>
      </>
    ) : null;

    return (
      <article key={item.id} className={articleClassNames.join(' ')}>
        {renderCardHeader({
          avatarNode,
          name: companyName,
          subtitle: 'Oferta publicada',
          timestampLabel: relativeTime ? `Publicada ${relativeTime}` : '',
          timestampDate: createdAt,
          typeLabel: 'Oferta',
          statusLabel,
          actions: headerActions,
        })}
        <div className="feed-card__shell-body feed-card__shell-body--publication">
          <div className="logistics-card">
            <div className="logistics-card__info">
              <div className="logistics-card__company">
                {profileHref ? (
                  <button type="button" onClick={() => navigate(profileHref)}>
                    {companyName}
                  </button>
                ) : (
                  companyName
                )}
              </div>
              <button
                type="button"
                className="logistics-card__title"
                onClick={() => navigate(publicationHref)}
              >
                {item.title || 'Publicacion sin titulo'}
              </button>
              {isEditing ? (
                <form
                  className="feed-card__edit-form"
                  onSubmit={event => handleSaveEditedPost(event, item, 'publication')}
                >
                  <label className="feed-card__edit-field" htmlFor={`feed-publication-edit-${item.id}`}>
                    <span className="visually-hidden">Editar descripcion de publicacion</span>
                    <textarea
                      id={`feed-publication-edit-${item.id}`}
                      name="content"
                      value={editingPostContent}
                      onChange={handleEditPostChange}
                      disabled={editingPostSaving}
                      rows={4}
                    />
                  </label>
                  {editingPostError ? (
                    <div className="feed-card__edit-error" role="alert">
                      {editingPostError}
                    </div>
                  ) : null}
                  <div className="feed-card__edit-actions">
                    <button
                      type="button"
                      className="feed-card__edit-cancel"
                      onClick={handleCancelEditPost}
                      disabled={editingPostSaving}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="feed-card__edit-save"
                      disabled={editingPostSaving}
                    >
                      {editingPostSaving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  </div>
                </form>
              ) : hasExcerpt ? (
                <button
                  type="button"
                  className="logistics-card__description"
                  onClick={() => navigate(publicationHref)}
                >
                  {displayedExcerpt}
                </button>
              ) : (
                <span className="logistics-card__description logistics-card__description--empty" aria-hidden="true" />
              )}
            </div>
            <figure className={visualClassNames.join(' ')}>
              <button
                type="button"
                className="logistics-card__visual-hit"
                onClick={() => navigate(publicationHref)}
                aria-label={item.title ? `Ver publicación ${item.title}` : 'Ver publicación'}
              >
                <img
                  src={imageSrc}
                  alt={item.title ? `Imagen de ${item.title}` : `Oferta publicada por ${companyName}`}
                  loading="lazy"
                />
                {locationLabel ? (
                  <span className="logistics-card__location-badge">
                    <FaMapMarkerAlt aria-hidden="true" />
                    {locationLabel}
                  </span>
                ) : null}
              </button>
            </figure>
          </div>
        </div>
        {renderCardFooter({
          priceLabel: priceDisplay,
          priceEyebrow,
          reacted,
          reactionTotal,
          onToggleReaction: () => handleToggleReaction('publication', item.id),
          onComment: () => navigate(publicationHref),
          primaryLabel: allowReservation ? 'Reservar' : 'Ver oferta',
          onPrimaryAction: allowReservation
            ? () => navigate(`${publicationHref}/reservar`)
            : () => navigate(publicationHref),
          primaryDisabled: false,
        })}
      </article>
    );
  }

  const renderEmptyState = (message, actionLabel) => (
    <div className="feed-empty">
      <strong>{message}</strong>
      <span>{actionLabel}</span>
    </div>
  );

  const renderSkeletonCards = count =>
    Array.from({ length: count }).map((_, index) => (
      <article key={`feed-skeleton-${index}`} className="feed-card feed-skeleton">
        <div className="feed-skeleton__header">
          <span className="feed-skeleton__avatar shimmer" />
          <div className="feed-skeleton__meta">
            <span className="feed-skeleton__line feed-skeleton__line--title shimmer" />
            <span className="feed-skeleton__line feed-skeleton__line--subtitle shimmer" />
          </div>
        </div>
        <div className="feed-skeleton__body">
          <span className="feed-skeleton__line shimmer" />
          <span className="feed-skeleton__line feed-skeleton__line--short shimmer" />
          <span className="feed-skeleton__line feed-skeleton__line--shorter shimmer" />
        </div>
        <div className="feed-skeleton__media shimmer" />
        <div className="feed-skeleton__footer">
          <span className="feed-skeleton__pill shimmer" />
          <span className="feed-skeleton__pill feed-skeleton__pill--wide shimmer" />
        </div>
      </article>
    ));

  const renderSkeleton = (count, { inline = false } = {}) => {
    const cards = renderSkeletonCards(count);
    if (inline) {
      return (
        <div className="feed-inline-skeleton" aria-hidden="true">
          {cards}
        </div>
      );
    }
    return (
      <div className="feed-list feed-list--loading" aria-hidden="true">
        {cards}
      </div>
    );
  };

  const handleQuickRequestCreated = newRequest => {
    if (!newRequest?.id) return;

    setRequests(prev => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return [newRequest];
      }
      const index = prev.findIndex(item => item.id === newRequest.id);
      if (index === -1) {
        return [newRequest, ...prev];
      }
      const next = [...prev];
      next[index] = newRequest;
      return next;
    });
  };

  const handleFeedPostCreated = post => {
    if (!post?.id) return;
    const normalized = { ...post };
    const empresaId = empresaValues?.id || empresaValues?.companyId || '';
    if (
      normalized.companyId &&
      (!normalized.company || !normalized.company.name) &&
      empresaValues?.name
    ) {
      normalized.company = {
        id: empresaValues.id || normalized.companyId,
        name: empresaValues.name,
        slug: empresaValues.slug || '',
        logoUrl: empresaValues.logoUrl || '',
        category: empresaValues.category || '',
        companyCategory: empresaValues.companyCategory || '',
        companyType: empresaValues.companyType || empresaValues.type || '',
        categories: Array.isArray(empresaValues.categories)
          ? empresaValues.categories.filter(Boolean)
          : [],
        isNews: false,
      };
    }

    if (!normalized.company && normalized.companyId) {
      normalized.company = {
        id: normalized.companyId,
        name: '',
        slug: '',
        logoUrl: '',
      };
    }

    const categorySet = new Set();
    const addCategory = value => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          categorySet.add(trimmed);
        }
      }
    };

    if (Array.isArray(normalized.companyCategories)) {
      normalized.companyCategories.forEach(addCategory);
    }
    addCategory(normalized.companyCategory);
    addCategory(normalized.companyType);
    collectCompanyCategories(normalized.company).forEach(addCategory);

    if (normalized.companyId && empresaId && normalized.companyId === empresaId) {
      if (Array.isArray(empresaValues?.categories)) {
        empresaValues.categories.forEach(addCategory);
      }
      addCategory(empresaValues?.category);
      addCategory(empresaValues?.companyCategory);
      addCategory(empresaValues?.companyType);
      addCategory(empresaValues?.type);
    }

    const combinedCategories = [...categorySet];
    const primaryCategory =
      normalized.companyCategory ||
      combinedCategories[0] ||
      '';
    const companyType =
      normalized.companyType ||
      empresaValues?.companyType ||
      empresaValues?.type ||
      '';

    normalized.companyCategory = primaryCategory;
    normalized.companyCategories = combinedCategories;
    normalized.companyType = companyType;

    if (normalized.company) {
      normalized.company = {
        ...normalized.company,
        category: normalized.company.category || primaryCategory,
        companyCategory: normalized.company.companyCategory || primaryCategory,
        companyType: normalized.company.companyType || companyType,
        categories: combinedCategories,
      };
    }

    normalized.newsLink =
      typeof normalized.newsLink === 'string' ? normalized.newsLink.trim() : '';
    if (!normalized.author || !normalized.author.name) {
      normalized.author = {
        id: user?.id || normalized.createdBy,
        name: user?.name || user?.email || 'Miembro de la red',
        email: user?.email || '',
        username: user?.username || '',
        avatarUrl: buildUserAvatarUrl(user, '120x120'),
      };
    } else {
      normalized.author = {
        ...normalized.author,
        avatarUrl: normalized.author.avatarUrl || buildUserAvatarUrl(user, '120x120'),
        username: normalized.author.username || user?.username || '',
      };
    }
    if (!normalized.created) {
      normalized.created = new Date().toISOString();
    }
    normalized.isNews = resolveIsNewsPost(normalized);
    if (normalized.company) {
      normalized.company.isNews = normalized.company.isNews || normalized.isNews;
    }
    setFeedPosts(prev => {
      const without = Array.isArray(prev) ? prev.filter(item => item.id !== normalized.id) : [];
      const next = [normalized, ...without];
      return next.sort(compareByCreationDesc);
    });
    setFeedNotification({ type: 'success', message: 'Publicacion creada.' });
  };

  useEffect(() => {
    if (!feedNotification.message) return;
    const timer = setTimeout(() => setFeedNotification({ type: '', message: '' }), 3500);
    return () => clearTimeout(timer);
  }, [feedNotification.message]);

  const handleFeedPostDeleted = postId => {
    const target = feedPosts.find(item => item.id === postId);
    if (!target) return;
    setConfirmingPost(target);
  };

  const confirmingPostLabel = useMemo(() => {
    if (!confirmingPost) return '';
    const pick = value => {
      if (typeof value !== 'string') return '';
      return value.trim();
    };
    const candidates = [
      pick(confirmingPost.title),
      pick(confirmingPost.headline),
      pick(confirmingPost.company?.name),
      pick(confirmingPost.content),
    ].filter(Boolean);
    if (!candidates.length) return '';
    const label = candidates[0];
    return label.length > 80 ? `${label.slice(0, 77).trim()}...` : label;
  }, [confirmingPost]);

  const handleConfirmDelete = async () => {
    if (!confirmingPost?.id) return;
    const postId = confirmingPost.id;
    setDeletingPostId(postId);
    setFeedPostError('');
    try {
      await deleteFeedPost(postId);
      setFeedPosts(prev => prev.filter(item => item.id !== postId));
      setFeedNotification({ type: 'success', message: 'Publicacion eliminada.' });
    } catch (error) {
      const message = error?.message || 'No pudimos eliminar la publicacion.';
      setFeedNotification({ type: 'error', message });
    } finally {
      setDeletingPostId('');
      setConfirmingPost(null);
    }
  };

  const resetEditingPostState = () => {
    setEditingPostId('');
    setEditingPostContent('');
    setEditingPostError('');
    setEditingPostSaving(false);
  };

  const handleToggleEditPost = (record, type = 'post') => {
    if (editingPostSaving) return;
    if (!record?.id) {
      resetEditingPostState();
      return;
    }
    if (editingPostId === record.id) {
      resetEditingPostState();
      return;
    }
    const initialContent =
      type === 'request'
        ? record.rawDetails || record.details || record.title || ''
        : type === 'publication'
          ? record.description || record.excerpt || record.title || ''
          : record.content || '';
    setEditingPostId(record.id);
    setEditingPostContent(initialContent);
    setEditingPostError('');
  };

  const handleEditPostChange = event => {
    setEditingPostContent(event.target.value);
  };

  const handleCancelEditPost = () => {
    if (editingPostSaving) return;
    resetEditingPostState();
  };

  const handleSaveEditedPost = async (event, record, type = 'post') => {
    event.preventDefault();
    if (!record?.id || editingPostId !== record.id) return;
    const trimmed = editingPostContent.trim();
    if (!trimmed) {
      setEditingPostError('Escribe algun mensaje antes de guardar.');
      return;
    }
    setEditingPostSaving(true);
    setEditingPostError('');
    try {
      if (type === 'post') {
        const updated = await updateFeedPost(record.id, { content: trimmed });
        setFeedPosts(prev =>
          prev.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
        );
        setFeedNotification({ type: 'success', message: 'Contenido actualizado.' });
      } else if (type === 'request') {
        const updated = await updateRequest(record.id, { details: trimmed });
        setRequests(prev =>
          prev.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
        );
        setFeedPosts(prev =>
          prev.map(postItem => {
            if (
              postItem.attachedRequestId === updated.id ||
              postItem.attachedRequest?.id === updated.id
            ) {
              return {
                ...postItem,
                attachedRequest: { ...(postItem.attachedRequest || {}), ...updated },
              };
            }
            return postItem;
          })
        );
        setFeedNotification({ type: 'success', message: 'Solicitud actualizada.' });
      } else if (type === 'publication') {
        const updated = await updatePublication(record.id, { description: trimmed });
        setPublications(prev =>
          prev.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
        );
        setFeedNotification({ type: 'success', message: 'Publicacion actualizada.' });
      }
      resetEditingPostState();
    } catch (error) {
      setEditingPostError(
        error?.message || 'No pudimos actualizar el contenido. Intenta nuevamente.'
      );
    } finally {
      setEditingPostSaving(false);
    }
  };

  const shouldRenderInitialSkeleton = showInitialFeedSkeleton;
  const shouldRenderFallbackSkeleton =
    !shouldRenderInitialSkeleton && combinedLoading && combinedFeedItems.length === 0;

  let mainContent;
  if (shouldRenderInitialSkeleton) {
    mainContent = renderSkeleton(INITIAL_FEED_SKELETON_COUNT);
  } else if (shouldRenderFallbackSkeleton) {
    mainContent = renderSkeleton(3);
  } else if (combinedFeedItems.length === 0) {
    const translatedError = translateGenericErrorMessage(combinedError);
    const message = translatedError || 'Sin actividad reciente';
    const hint = translatedError
      ? 'Recarga la pagina y vuelve a intentarlo en unos minutos.'
      : 'Publica contenido o crea requerimientos para verlos aqui.';
    mainContent = renderEmptyState(message, hint);
  } else {
    mainContent = (
      <div className="feed-list">
        {visibleFeedItems.map((item, index) => {
          let rendered = null;
          if (item.type === 'post') {
            rendered = renderFeedPostCard(item.payload);
          } else if (item.type === 'request') {
            rendered = renderRequestCard(item.payload);
          } else if (item.type === 'publication') {
            rendered = renderPublicationCard(item.payload);
          }
          return (
            <React.Fragment key={item.key}>
              {rendered}
              {index === FEED_PAGE_SIZE - 1 && visibleFeedItems.length >= FEED_PAGE_SIZE ? (
                <span
                  ref={afterFirstBatchRef}
                  className="feed-scroll-sentinel"
                  aria-hidden="true"
                />
              ) : null}
            </React.Fragment>
          );
        })}
        {canLoadMoreFeed ? (
          <div
            ref={loadMoreTriggerRef}
            className="feed-list__load-more feed-list__load-more--auto"
            role="status"
            aria-live="polite"
          >
            <span className="visually-hidden">
              {combinedLoading
                ? 'Cargando más actividad'
                : 'Continua desplazandote o usa el boton para mostrar mas actividad'}
            </span>
            <div
              className="feed-list__load-more-skeleton"
              aria-hidden="true"
              data-loading={combinedLoading ? 'true' : 'false'}
            >
              {renderSkeleton(1, { inline: true })}
            </div>
            <button
              type="button"
              className="feed-list__load-more-button feed-list__load-more-button--ghost"
              onClick={handleLoadMoreFeedItems}
              disabled={combinedLoading}
            >
              {combinedLoading ? 'Cargando…' : 'Mostrar mas actividad'}
            </button>
          </div>
        ) : null}
        {reachedFeedLimit ? (
          <p className="feed-list__limit-hint">
            Has llegado al limite de {MAX_FEED_ITEMS} actividades recientes.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <section className="dashboard-feed">
      <div className="dashboard-feed__layout">
        <div className="feed-main">
          {feedNotification.message ? (
            <div
              className={`feed-toast feed-toast--${feedNotification.type === 'error' ? 'error' : 'success'}`}
              role="status"
            >
              {feedNotification.message}
            </div>
          ) : null}

          {feedPostError && !feedNotification.message ? (
            <div className="feed-toast feed-toast--error" role="alert">
              {feedPostError}
            </div>
          ) : null}

          {proposalTarget ? (
            <div className="feed-dialog-backdrop" role="presentation">
              <div
                className="feed-dialog feed-dialog--proposal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="feed-proposal-title"
              >
                <div className="feed-proposal__header">
                  <h2 id="feed-proposal-title">Enviar propuesta</h2>
                  <p>
                    Atiende la solicitud{' '}
                    <strong>{proposalTarget.title || 'sin titulo'}</strong> con los detalles de tu
                    servicio.
                  </p>
                </div>
                <form className="feed-proposal__form" onSubmit={handleSubmitProposal}>
                  <div className="feed-proposal__group">
                    <label htmlFor="proposal-cost">Costo ofertado</label>
                    <input
                      id="proposal-cost"
                      name="cost"
                      type="text"
                      placeholder="Ej. $28,500 MXN"
                      value={proposalForm.cost}
                      onChange={handleProposalFieldChange}
                    />
                  </div>
                  <div className="feed-proposal__group">
                    <label htmlFor="proposal-provider-name">Nombre del contacto</label>
                    <input
                      id="proposal-provider-name"
                      name="providerName"
                      type="text"
                      placeholder="Ej. Laura Martínez"
                      value={proposalForm.providerName}
                      onChange={handleProposalFieldChange}
                      autoComplete="name"
                    />
                  </div>
                  <div className="feed-proposal__group">
                    <label htmlFor="proposal-provider-email">Correo electrónico</label>
                    <input
                      id="proposal-provider-email"
                      name="providerEmail"
                      type="email"
                      placeholder="correo@tuempresa.com"
                      value={proposalForm.providerEmail}
                      onChange={handleProposalFieldChange}
                      autoComplete="email"
                    />
                  </div>
                  <div className="feed-proposal__group">
                    <label htmlFor="proposal-provider-phone">Teléfono de contacto</label>
                    <input
                      id="proposal-provider-phone"
                      name="providerPhone"
                      type="tel"
                      placeholder="Ej. +52 55 1234 5678"
                      value={proposalForm.providerPhone}
                      onChange={handleProposalFieldChange}
                      autoComplete="tel"
                    />
                    <small>Incluye lada y extensión si aplica.</small>
                  </div>
                  <div className="feed-proposal__group">
                    <label htmlFor="proposal-notes">Detalle adicional</label>
                    <textarea
                      id="proposal-notes"
                      name="notes"
                      rows={4}
                      placeholder="Describe disponibilidad, cobertura, tiempos de respuesta u otros datos relevantes."
                      value={proposalForm.notes}
                      onChange={handleProposalFieldChange}
                    />
                  </div>
                  {proposalFeedback.message ? (
                    <p
                      className={`feed-proposal__feedback feed-proposal__feedback--${proposalFeedback.tone ||
                        'info'}`}
                    >
                      {proposalFeedback.message}
                    </p>
                  ) : null}
                  <div className="feed-proposal__actions">
                    <button
                      type="button"
                      className="feed-dialog__secondary"
                      onClick={handleCloseProposal}
                      disabled={proposalSubmitting}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="feed-proposal__submit"
                      disabled={proposalSubmitting}
                    >
                      {proposalSubmitting ? 'Enviando...' : 'Enviar propuesta'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}

          {confirmingPost ? (
            <div className="feed-dialog-backdrop" role="presentation">
              <div
                className="feed-dialog feed-dialog--danger"
                role="dialog"
                aria-modal="true"
                aria-labelledby="feed-dialog-title"
              >
                <div className="feed-dialog__icon" aria-hidden="true">
                  <FiAlertTriangle />
                </div>
                <h2 id="feed-dialog-title">¿Eliminar Publicación?</h2>
                <p className="feed-dialog__body">
                  Estás a punto de eliminar{' '}
                  <span className="feed-dialog__target">
                    {confirmingPostLabel || 'esta publicación'}
                  </span>
                  . Esta acción es irreversible.
                </p>
                {confirmingPost.content ? (
                  <blockquote className="feed-dialog__preview">
                    {confirmingPost.content.length > 140
                      ? `${confirmingPost.content.slice(0, 137).trim()}...`
                      : confirmingPost.content}
                  </blockquote>
                ) : null}
                <div className="feed-dialog__actions">
                  <button
                    type="button"
                    className="feed-dialog__secondary"
                    onClick={() => (deletingPostId ? null : setConfirmingPost(null))}
                    disabled={Boolean(deletingPostId)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="feed-dialog__danger"
                    onClick={handleConfirmDelete}
                    disabled={Boolean(deletingPostId)}
                  >
                    {deletingPostId ? 'Eliminando...' : 'Sí, Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <FeedComposer
            user={user}
            company={empresaValues}
            onRequestCreated={handleQuickRequestCreated}
            onPostCreated={handleFeedPostCreated}
          />

          <header className="feed-header">
            <div className="feed-header__divider-row">
              <span className="feed-header__divider" aria-hidden="true" />
              <button
                type="button"
                className="feed-header__sort"
                aria-haspopup="listbox"
                aria-expanded="false"
              >
                <span className="feed-header__sort-label">Ordenar por</span>
                <strong>Destacados</strong>
                <FaChevronDown aria-hidden="true" />
              </button>
            </div>
          </header>

          {mainContent}
        </div>
        <aside className="feed-sidebar">
          {shouldRenderPlanCard ? (
            showSubscriptionCTA ? (
              <a href={subscriptionHref} className="sidebar-widget sidebar-widget--premium">
                <div className="sidebar-widget__header">
                  <div className="sidebar-widget__icon" aria-hidden="true">
                    <FiStar focusable="false" />
                  </div>
                  <div className="sidebar-widget__text">
                    <span className="sidebar-widget__kicker">Beneficios premium</span>
                    <strong className="sidebar-widget__title">Plan Premium</strong>
                  </div>
                </div>
                <span className="sidebar-widget__button sidebar-widget__button--premium">
                  Hazte premium
                </span>
              </a>
            ) : (
              <a
                href={subscriptionHref}
                className={`sidebar-widget sidebar-widget--premium sidebar-widget--premium-active${subscriptionPlanClass}`}
              >
                <div className="sidebar-widget__header">
                  <div className="sidebar-widget__icon" aria-hidden="true">
                    <FiStar focusable="false" />
                  </div>
                  <div className="sidebar-widget__text">
                    <span className="sidebar-widget__kicker">Plan actual</span>
                    <strong className="sidebar-widget__title">Plan Premium</strong>
                  </div>
                </div>
                <span className="sidebar-widget__button sidebar-widget__button--premium">
                  Ver beneficios
                </span>
              </a>
            )
          ) : null}
          {showVerificationReminder ? (
            <a href={verificationHref} className="sidebar-widget sidebar-widget--verification">
              <div className="sidebar-widget__header">
                <div className="sidebar-widget__icon" aria-hidden="true">
                  <FiShield focusable="false" />
                </div>
                <div className="sidebar-widget__text">
                  <span className="sidebar-widget__kicker">Verificación pendiente</span>
                  <strong className="sidebar-widget__title">Verificación</strong>
                  <span className="sidebar-widget__status">Pendiente</span>
                </div>
              </div>
              <span className="sidebar-widget__button sidebar-widget__button--verification">
                Ir a verificación
              </span>
            </a>
          ) : null}
          <FeedNewsList
            items={newsPostItems}
            loading={loadingFeedPosts && !newsPostItems.length}
            error={feedPostError}
            formatRelativeTime={formatRelativeTime}
            onOpenCompany={handleOpenCompanyProfile}
            onViewAllNews={handleViewAllNews}
          />
          <MarketplaceSpotlightCard onSelect={handleMarketplaceSpotClick} />
          <CompanySpotlightCard
            companies={companySpotlightItems}
            onSelect={handleCompanySpotlightClick}
          />
        </aside>
      </div>
      <button
        type="button"
        className={`feed-scroll-top${showScrollTop ? ' feed-scroll-top--visible' : ''}`}
        onClick={handleScrollTopClick}
        aria-label="Regresar arriba"
      >
        <FaArrowUp aria-hidden="true" />
      </button>
    </section>
  );
}
